import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(form.email, form.password);
      toast.success(`Welcome back, ${u.name.split(" ")[0]}`);
      nav(`/${u.role}`);
    } catch (err) {
      const msg = err.response?.data?.detail || "Login failed";
      if (msg === "Email not verified") { nav(`/verify?email=${encodeURIComponent(form.email)}`); }
      toast.error(msg);
    } finally { setLoading(false); }
  };

  return (
    <AuthShell title="Welcome back" subtitle="Log in to your SELLEXR workspace.">
      <form onSubmit={submit} className="space-y-4">
        <div>
          <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Email</label>
          <Input data-testid="login-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" />
        </div>
        <div>
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-slate-600 uppercase tracking-wider">Password</label>
            <Link to="/forgot" className="text-xs text-brand" data-testid="login-forgot">Forgot?</Link>
          </div>
          <Input data-testid="login-password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="mt-1.5" />
        </div>
        <Button data-testid="login-submit" disabled={loading} className="btn-orange hover:bg-[#FF6A00] w-full py-6">
          {loading ? "Logging in..." : "Log in"}
        </Button>
        <p className="text-sm text-slate-600 text-center">
          New here? <Link to="/signup" className="text-brand font-semibold" data-testid="login-signup-link">Create an account</Link>
        </p>
      </form>
    </AuthShell>
  );
}

export function AuthShell({ title, subtitle, children }) {
  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden" style={{ background: "linear-gradient(135deg,#155EEF 0%,#0B3FB8 100%)" }}>
        <div className="absolute inset-0 opacity-30" style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #FF6A00 0%, transparent 40%)" }} />
        <div className="relative p-12 h-full flex flex-col justify-between text-white">
          <Link to="/"><Logo variant="white" size="lg" /></Link>
          <div>
            <div className="eyebrow" style={{ color: "#FF6A00" }}>SELLEXR</div>
            <h2 className="mt-3 font-heading font-bold text-4xl leading-tight">The workforce layer for Indian e-commerce.</h2>
            <p className="mt-4 text-white/70">Hire, train, and manage certified freelancers to grow your online sales.</p>
          </div>
          <div className="text-sm text-white/60">© 2026 SELLEXR</div>
        </div>
      </div>
      <div className="flex items-center justify-center p-8 lg:p-12 bg-white">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8"><Link to="/"><Logo /></Link></div>
          <h1 className="font-heading font-bold text-3xl">{title}</h1>
          <p className="mt-2 text-slate-600">{subtitle}</p>
          <div className="mt-8">{children}</div>
        </div>
      </div>
    </div>
  );
}
